import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getBootstrap,
  login as loginService,
  rsminLogin as rsminLoginService,
  register as registerService,
  logoutApi,
} from '../services/authService';

const AuthContext = createContext(null);

// Empty session shape — mirrors the bootstrap response contract exactly.
// Every consumer can destructure with a stable shape regardless of load state.
const EMPTY_SESSION = {
  user:             null,
  panel:            null,   // 'admin' | 'staff' | 'proponent' | 'community' | 'user' | 'genpop'
  memberships:      [],
  activeTenant:     null,
  activeMembership: null,
  allowedModules:   {},
  access:           null,
};

// Helpers to keep token storage in one place
const storeTokens = (token, refreshToken, userId) => {
  localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  if (userId)       localStorage.setItem('userId', String(userId));
};

const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
};

export const AuthProvider = ({ children }) => {
  const [session, setSession]   = useState(EMPTY_SESSION);
  const [loading, setLoading]   = useState(true);

  // Single function that fills the session from the backend bootstrap response.
  // Called on mount (if token present), after login, and after register.
  const loadSession = useCallback(async () => {
    try {
      const res = await getBootstrap();
      setSession(res.data);
    } catch {
      // Token invalid or expired — wipe it so the user is redirected to login
      clearTokens();
      setSession(EMPTY_SESSION);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      loadSession().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadSession]);

  const login = useCallback(async (email, password) => {
    const res = await loginService({ email, password });
    storeTokens(res.data.token, res.data.refreshToken, res.data.user?._id);
    await loadSession();
  }, [loadSession]);

  const register = useCallback(async (data) => {
    const res = await registerService(data);
    storeTokens(res.data.token, res.data.refreshToken, res.data.user?._id);
    await loadSession();
  }, [loadSession]);

  const loginWithRSMinToken = useCallback(async (rsminToken) => {
    const res = await rsminLoginService(rsminToken);
    storeTokens(res.data.token, res.data.refreshToken, res.data.user?._id);
    await loadSession();
  }, [loadSession]);

  const logout = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    try { await logoutApi(userId); } catch { /* server-side invalidation best-effort */ }
    clearTokens();
    setSession(EMPTY_SESSION);
  }, []);

  // Convenience: check if a named module is enabled for the current user
  const isModuleEnabled = useCallback((moduleName) => {
    return !!(session.allowedModules && session.allowedModules[moduleName]);
  }, [session.allowedModules]);

  const value = {
    // Full bootstrap payload (destructure whatever you need)
    ...session,
    // Auth state
    loading,
    // Actions
    login,
    loginWithRSMinToken,
    register,
    logout,
    // Helpers
    isModuleEnabled,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

